///
/// EarthBrowser
///
/// Buoys & coastal stations legend body: the sea-state marker anatomy and
/// the two color scales, drawn as plan-view SVG schematics of the 3D
/// pieces in BuoyMarkerNode; the wave palette mirrors getWaveHeightColor
/// and the wind palette getWindSpeedColor in BuoyNode - keep them in sync.
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///

import { ColorScale, LegendFootnote, LegendSection, SymbolRow } from "./LegendPrimitives"

// Wave height steps from BuoyNode.getWaveHeightColor: [meters, css]
const cWaveStops: Array<[number, string]> = [
	[0, "rgb(51, 153, 255)"],
	[1, "rgb(77, 204, 128)"],
	[2, "rgb(128, 230, 77)"],
	[3, "rgb(230, 230, 51)"],
	[4, "rgb(255, 153, 26)"],
	[5, "rgb(255, 51, 51)"],
]

// Wind steps from BuoyNode.getWindSpeedColor: [m/s, css]
const cWindStops: Array<[number, string]> = [
	[0, "rgb(199, 209, 230)"],
	[3.4, "rgb(224, 230, 199)"],
	[8, "rgb(250, 217, 102)"],
	[13.9, "rgb(255, 153, 51)"],
	[20.8, "rgb(255, 64, 51)"],
]

const cInk = "rgba(5, 13, 23, 0.7)"
const cShaft = "rgb(245, 250, 255)"
const cGreen = cWaveStops[2][1]
const cCoastal = cWindStops[2][1]
const cSilent = "rgba(140, 153, 173, 0.9)"
const cTide = "rgb(115, 158, 217)"
const cDart = "rgba(166, 128, 230, 0.95)"
const cPulse = "rgb(255, 184, 64)"
const cCluster = "rgba(255, 255, 255, 0.6)"

function stepGradient(stops: Array<[number, string]>, max: number): string {
	const parts: string[] = []
	for (let i = 0; i < stops.length; i++) {
		const start = (stops[i][0] / max) * 100
		const end = i + 1 < stops.length ? (stops[i + 1][0] / max) * 100 : 100
		parts.push(`${stops[i][1]} ${start.toFixed(1)}%`, `${stops[i][1]} ${end.toFixed(1)}%`)
	}
	return `linear-gradient(to right, ${parts.join(", ")})`
}

const cWaveMax = 6
const cWindMax = 25
const cWaveGradient = stepGradient(cWaveStops, cWaveMax)
const cWindGradient = stepGradient(cWindStops, cWindMax)

interface SeaStateGlyphProps {
	r: number
	color?: string
	tail?: boolean
	ring?: number
	shaft?: number
	pulse?: boolean
	cluster?: boolean
}

/** Sea-state buoy symbol, north up; the tail points toward where waves come from (drawn from the SW) */
function SeaStateGlyph({ r, color = cGreen, tail = false, ring = 0, shaft = 0, pulse = false, cluster = false }: SeaStateGlyphProps) {
	const size = 36
	const c = size / 2
	// Swell from 225 deg (SW): screen vector (-sin, +cos) with y down
	const dir = { x: -Math.SQRT1_2, y: Math.SQRT1_2 }
	const perp = { x: dir.y, y: -dir.x }
	const base = r * 0.4
	const tip = r * 2.0
	const halfBase = r * 0.62
	const tailPoints = [
		`${c + dir.x * base + perp.x * halfBase},${c + dir.y * base + perp.y * halfBase}`,
		`${c + dir.x * base - perp.x * halfBase},${c + dir.y * base - perp.y * halfBase}`,
		`${c + dir.x * tip},${c + dir.y * tip}`,
	].join(" ")
	const ringR = r + 2.5 + ring * 0.5
	// Wind from the NW
	const wdir = { x: -Math.SQRT1_2, y: -Math.SQRT1_2 }
	const shaftStart = r + (ring > 0 ? 5.5 : 2.5)

	return (
		<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
			{pulse && <circle cx={c} cy={c} r={r + 8} fill="none" stroke={cPulse} strokeWidth={1.6} opacity={0.7} />}
			{cluster && <circle cx={c} cy={c} r={r + 5.5} fill="none" stroke={cCluster} strokeWidth={0.9} />}
			{ring > 0 && <circle cx={c} cy={c} r={ringR} fill="none" stroke={color} strokeWidth={ring} opacity={0.65} />}
			{tail && <polygon points={tailPoints} fill={color} stroke={cInk} strokeWidth={1} strokeLinejoin="round" />}
			<circle cx={c} cy={c} r={r} fill={color} stroke={cInk} strokeWidth={1} />
			<circle cx={c - r * 0.3} cy={c - r * 0.3} r={r * 0.28} fill="white" opacity={0.22} />
			{shaft > 0 && (
				<line
					x1={c + wdir.x * shaftStart} y1={c + wdir.y * shaftStart}
					x2={c + wdir.x * (shaftStart + shaft)} y2={c + wdir.y * (shaftStart + shaft)}
					stroke={cShaft} strokeWidth={1.5} strokeLinecap="round"
				/>
			)}
		</svg>
	)
}

function CoastalGlyph({ shaft = 8, color = cCoastal }: { shaft?: number; color?: string }) {
	const size = 36
	const c = size / 2
	const r = 5
	const points = [
		`${c},${c - r}`,
		`${c + r * 0.866},${c + r * 0.5}`,
		`${c - r * 0.866},${c + r * 0.5}`,
	].join(" ")
	const wdir = { x: Math.SQRT1_2, y: -Math.SQRT1_2 }
	const start = r + 2.5
	return (
		<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
			<polygon points={points} fill={color} stroke={cInk} strokeWidth={1} strokeLinejoin="round" />
			{shaft > 0 && (
				<line
					x1={c + wdir.x * start} y1={c + wdir.y * start}
					x2={c + wdir.x * (start + shaft)} y2={c + wdir.y * (start + shaft)}
					stroke={cShaft} strokeWidth={1.5} strokeLinecap="round"
				/>
			)}
		</svg>
	)
}

function SilentGlyph() {
	return (
		<svg width={36} height={36} viewBox="0 0 36 36" className="shrink-0">
			<circle cx={18} cy={18} r={3.5} fill="none" stroke={cSilent} strokeWidth={1.1} />
		</svg>
	)
}

function DartGlyph({ solid = false }: { solid?: boolean }) {
	return (
		<svg width={36} height={36} viewBox="0 0 36 36" className="shrink-0">
			<polygon
				points="18,13.5 22.5,18 18,22.5 13.5,18"
				fill={solid ? cDart : "none"}
				stroke={solid ? cInk : cDart}
				strokeWidth={solid ? 1 : 1.3}
				strokeLinejoin="round"
			/>
		</svg>
	)
}

export function BuoyLegendContent() {
	return (
		<>
			<ColorScale
				label="Buoys · significant wave height"
				gradient={cWaveGradient}
				ticks={[
					{ at: 0, label: "0 m" },
					{ at: (2 / cWaveMax) * 100, label: "2" },
					{ at: (4 / cWaveMax) * 100, label: "4" },
					{ at: 100, label: "5+ m" },
				]}
			/>
			<div className="space-y-1">
				<div className="text-slate-300">Size · wave height</div>
				<div className="flex items-end gap-3 px-1">
					{[[0.5, 5.25], [2, 7.5], [5, 12]].map(([m, px]) => (
						<span key={m} className="flex flex-col items-center gap-0.5">
							<SeaStateGlyph r={px} color={cWaveStops[Math.min(5, Math.floor(m))][1]} />
							<span className="text-[10px] text-slate-400">{m} m</span>
						</span>
					))}
				</div>
			</div>
			<LegendSection>
				<SymbolRow swatch={<SeaStateGlyph r={7} tail />}>Wedge points into the swell: where the waves come from</SymbolRow>
				<SymbolRow swatch={<SeaStateGlyph r={7} ring={3.2} />}>Ring thickness · dominant period; it breathes at that period</SymbolRow>
				<SymbolRow swatch={<SeaStateGlyph r={7} shaft={9} />}>Shaft points into the wind, length · speed; cross-bar at gale force</SymbolRow>
				<SymbolRow swatch={<SeaStateGlyph r={7} pulse />}>Pulsing dome · seas over 5 m, storm-force wind, or pressure falling fast</SymbolRow>
			</LegendSection>
			<LegendSection>
				<ColorScale
					label="Coastal stations · wind speed"
					gradient={cWindGradient}
					ticks={[
						{ at: 0, label: "calm" },
						{ at: (8 / cWindMax) * 100, label: "8" },
						{ at: (13.9 / cWindMax) * 100, label: "14" },
						{ at: 100, label: "21+ m/s" },
					]}
				/>
				<SymbolRow swatch={<CoastalGlyph />}>Coastal / platform station (cone): wind only, no waves</SymbolRow>
				<SymbolRow swatch={<CoastalGlyph shaft={0} color={cTide} />}>Tide gauge: water level only</SymbolRow>
				<SymbolRow swatch={<SilentGlyph />}>Nothing reported in 30 days</SymbolRow>
				<SymbolRow swatch={<DartGlyph solid />}>DART tsunameter reporting water-column height; faint when it hasn't, red and pulsing in event mode</SymbolRow>
			</LegendSection>
			<LegendFootnote>
				NOAA NDBC hourly met and spectral wave summaries, 15-minute DART water column; NOAA CO-OPS tide gauges and coastal met every half hour; IMOS coastal wave buoys (Australia, CC-BY); Cefas WaveNet (UK, OGL v3). Markers shrink to dots from orbit; zoom in to read them.
			</LegendFootnote>
		</>
	)
}
